import React, { useState, useCallback } from "react";
import { View, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { PageFooter } from "@/components/ui/PageFooter";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { localizationApi } from "@/lib/api/localization";
import type {
  ContentType,
  LocalizationKeyDetail,
  LocalizationActivityEntry,
} from "@/lib/api/localization";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { EditTranslationModal } from "./components/EditTranslationModal";

// ─── Activity Log Entry ───────────────────────────────────────────────────────

const ACTIVITY_ACTION_I18N: Record<LocalizationActivityEntry["actionType"], TranslationKeys> = {
  add: "staff.localization.detail.activityAction.add",
  edit: "staff.localization.detail.activityAction.edit",
  delete: "staff.localization.detail.activityAction.delete",
};

const ACTIVITY_TYPE_CONFIG: Record<
  LocalizationActivityEntry["actionType"],
  { iconName: string; bgClass: string }
> = {
  edit: { iconName: "edit-2", bgClass: "bg-blue-50" },
  add: { iconName: "plus", bgClass: "bg-green-40" },
  delete: { iconName: "trash-2", bgClass: "bg-red-50" },
};

interface ActivityEntryProps {
  entry: LocalizationActivityEntry;
  isLast: boolean;
  locale: string;
  t: ReturnType<typeof useI18n>["t"];
}

function ActivityEntry({ entry, isLast, locale, t }: ActivityEntryProps) {
  const config = ACTIVITY_TYPE_CONFIG[entry.actionType] ?? {
    iconName: "activity",
    bgClass: "bg-stroke-70",
  };
  const { date, time } = formatSplitDateTime(entry.createdAt, locale as any);

  return (
    <View className="flex-row gap-4">
      <View className="items-center w-8">
        <View className={`w-8 h-8 rounded-full items-center justify-center ${config.bgClass}`}>
          <Icon as={Feather} name={config.iconName as any} size={16} className="text-white" />
        </View>
        {!isLast && <View className="flex-1 w-px bg-stroke-30 mt-1" />}
      </View>
      <View className={`flex-1 gap-1 ${!isLast ? "pb-8" : ""}`}>
        <View className="flex-row flex-wrap gap-x-1 items-baseline">
          <AppText weight="semibold" className="text-lg text-dark-text-50">
            {entry.actorName}
          </AppText>
          <AppText className="text-lg text-supporting-text">
            {t(ACTIVITY_ACTION_I18N[entry.actionType])} · {entry.localeLabel}
          </AppText>
        </View>
        <AppText className="text-base text-supporting-text">
          {formatRelativeTime(entry.createdAt, locale as any, t)}
        </AppText>
        <View className="flex-row items-center gap-1">
          <AppText className="text-sm text-supporting-text-40">{date}</AppText>
          <View className="w-1 h-1 rounded-full bg-supporting-text-40" />
          <AppText className="text-sm text-supporting-text-40">{time}</AppText>
        </View>
      </View>
    </View>
  );
}

// ─── Translation Row ──────────────────────────────────────────────────────────

interface TranslationRowProps {
  language: string;
  value: string;
  onEdit: () => void;
  isLast: boolean;
  editLabel: TranslationKeys;
}

function TranslationRow({ language, value, onEdit, isLast, editLabel }: TranslationRowProps) {
  return (
    <View
      className={`flex-row items-start py-4 gap-4 tablet:gap-10 ${!isLast ? "border-b border-stroke-50" : ""}`}
    >
      <AppText
        weight="medium"
        className="text-start w-28 text-base text-supporting-text"
        numberOfLines={1}
      >
        {language}
      </AppText>
      <AppText className="flex-1 text-base text-dark-text-50">{value || "—"}</AppText>
      <Button
        label={editLabel}
        variant="secondary"
        onPress={onEdit}
        labelClassName="text-primary"
      />
    </View>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

/**
 * Detail page for a single localization key.
 *
 * Shows: breadcrumb, page header with key title + last edited + Edit Translations button,
 * Translation Details card, Activity Log card (hidden when empty — no backend support yet).
 *
 * Route: (staff)/localization/[key]
 * Matches Figma "Staff / Localization / Translation Detail" composition.
 */
export function TranslationDetailScreen() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const {
    key,
    contentType: contentTypeParam,
    id,
  } = useLocalSearchParams<{
    key: string;
    contentType?: string;
    id?: string;
  }>();

  const decodedKey = key ? decodeURIComponent(key) : "";
  const contentType = (contentTypeParam ?? "other") as ContentType;

  const contentTypeLabelKey =
    contentType === "categories"
      ? "staff.localization.detail.contentTypeCategories"
      : contentType === "industries"
        ? "staff.localization.detail.contentTypeIndustries"
        : "staff.localization.detail.contentTypeOther";

  const [editAllVisible, setEditAllVisible] = useState(false);
  const [editLocale, setEditLocale] = useState<"en" | "zh-CN" | "ar" | null>(null);

  // ── Fetch translations ─────────────────────────────────────────────────────

  const fetchDetail = useCallback(async (): Promise<LocalizationKeyDetail> => {
    if (contentType === "other") {
      return localizationApi.getOtherKeyTranslations(decodedKey);
    }

    // For categories / industries: fetch the full list and find by id or key
    const items =
      contentType === "industries"
        ? await localizationApi.listIndustriesAll()
        : await localizationApi.listCategoriesAll();

    const match = id ? items.find((i) => i.id === id) : items.find((i) => i.key === decodedKey);

    if (!match) {
      return {
        key: decodedKey,
        lastEditedAt: null,
        translations: { en: null, "zh-CN": null, ar: null },
        contentType,
        activityLog: [],
      };
    }

    return {
      key: match.key,
      lastEditedAt: match.lastEditedAt,
      translations: match.translations,
      contentType,
      activityLog: [],
    };
  }, [contentType, decodedKey, id]);

  const { data } = useQuery({
    queryKey: ["localization", "detail", contentType, id ?? decodedKey],
    queryFn: fetchDetail,
    enabled: !!decodedKey,
  });

  const { data: activityData } = useQuery({
    queryKey: ["localization", "activity", contentType, id ?? decodedKey],
    queryFn: () => localizationApi.getActivityLog(id ?? decodedKey),
    enabled: contentType === "other" && !!decodedKey,
  });

  const relativeTime = data?.lastEditedAt
    ? formatRelativeTime(data.lastEditedAt, locale as any, t)
    : "—";

  // ── Edit handlers ──────────────────────────────────────────────────────────

  const handleEditAll = useCallback(() => {
    setEditLocale(null);
    setEditAllVisible(true);
  }, []);

  const handleEditLocale = useCallback((loc: "en" | "zh-CN" | "ar") => {
    setEditLocale(loc);
    setEditAllVisible(true);
  }, []);

  const handleDismissEdit = useCallback(() => {
    setEditAllVisible(false);
    setEditLocale(null);
  }, []);

  const handleSave = useCallback(
    async (values: { en: string; zhCn: string; ar: string }) => {
      if (contentType === "industries" && id) {
        await localizationApi.updateIndustryTranslations(id, values);
        await queryClient.invalidateQueries({ queryKey: ["localization", "industries"] });
      } else if (contentType === "categories" && id) {
        await localizationApi.updateCategoryTranslations(id, values);
        await queryClient.invalidateQueries({ queryKey: ["localization", "categories"] });
      } else if (contentType === "other") {
        await localizationApi.updateOtherTranslations(decodedKey, values);
        await queryClient.invalidateQueries({ queryKey: ["localization", "other"] });
        await queryClient.invalidateQueries({ queryKey: ["localization", "statistics"] });
      } else {
        // Fail fast: unexpected combination (e.g., industries/categories without id).
        // Do not attempt to save as an "other" translation to avoid corrupting data.
        console.warn("TranslationDetailScreen.handleSave called with invalid state:", {
          contentType,
          id,
          decodedKey,
        });
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: ["localization", "detail", contentType, id ?? decodedKey],
      });
      await queryClient.invalidateQueries({
        queryKey: ["localization", "activity", contentType, id ?? decodedKey],
      });
    },
    [contentType, id, decodedKey, queryClient]
  );

  const editInitialValues = {
    initialEn: data?.translations.en ?? "",
    initialZhCn: data?.translations["zh-CN"] ?? "",
    initialAr: data?.translations.ar ?? "",
  };

  const activityLog = activityData?.items ?? [];

  return (
    <View className="flex-1 bg-stroke-20">
      <ScrollView className="flex-1 bg-stroke-10" contentContainerStyle={{ flexGrow: 1 }}>
        {/* ── Breadcrumb ──────────────────────────────────────── */}
        <View className="px-page-x bg-white py-4 border-b border-stroke-50">
          <Breadcrumb
            segments={[
              {
                labelText: t(contentTypeLabelKey),
                href: "/(staff)/(dashboard)/localization",
              },
              { labelText: decodedKey },
            ]}
          />
        </View>

        {/* ── Page header ─────────────────────────────────────── */}
        <View className="bg-white border-b border-stroke-50 py-6">
          <View className="px-page-x tablet:px-20 w-full">
            <View className="w-full flex-row flex-wrap items-start justify-between gap-4">
              <View>
                <AppText
                  weight="semibold"
                  className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
                >
                  {decodedKey}
                </AppText>
                <AppText className="text-base text-supporting-text mt-1">
                  {`${t("staff.localization.card.lastEdited")} ${relativeTime}`}
                </AppText>
              </View>
              <Button
                label={"staff.localization.detail.editTranslations"}
                variant="primary"
                onPress={handleEditAll}
              />
            </View>
          </View>
        </View>

        {/* ── Content ─────────────────────────────────────────── */}
        <View className="px-page-x tablet:px-20 py-8 gap-6">
          {/* Translation Details card */}
          <View className="border border-stroke-50 rounded-xl overflow-hidden bg-white">
            {/* Card header */}
            <View className="items-start px-6 py-4 border-b border-stroke-50">
              <AppText weight="semibold" className="text-lg text-dark-text-50">
                {t("staff.localization.detail.title")}
              </AppText>
            </View>
            <ScrollView horizontal contentContainerStyle={{ flexGrow: 1 }}>
              <View className="w-full items-start">
                {/* Key Title + Content Type metadata row */}
                <View className="w-full flex-row ite px-6 py-4 gap-4 tablet:gap-10 border-b border-stroke-50">
                  <View className="flex-1 items-start">
                    <AppText
                      weight="medium"
                      className="text-xs text-supporting-text uppercase tracking-wider mb-1"
                    >
                      {t("staff.localization.detail.keyTitle")}
                    </AppText>
                    <AppText weight="medium" className="text-base text-dark-text-50">
                      {decodedKey}
                    </AppText>
                  </View>
                  <View className="flex-1 items-start">
                    <AppText
                      weight="medium"
                      className="text-xs text-supporting-text uppercase tracking-wider mb-1"
                    >
                      {t("staff.localization.detail.contentType")}
                    </AppText>
                    <AppText weight="medium" className="text-base text-dark-text-50">
                      {t(contentTypeLabelKey)}
                    </AppText>
                  </View>
                </View>

                {/* Column headers */}
                <View className="w-full flex-row items-start px-6 py-3 bg-stroke-10 gap-4 tablet:gap-10 border-b border-stroke-50">
                  <AppText
                    weight="medium"
                    className="text-start w-28 text-xs text-supporting-text uppercase tracking-wider"
                  >
                    {t("staff.localization.detail.language")}
                  </AppText>
                  <AppText
                    weight="medium"
                    className="flex-1 text-start text-xs text-supporting-text uppercase tracking-wider"
                  >
                    {t("staff.localization.detail.translation")}
                  </AppText>
                  <View className="w-20" />
                </View>

                {/* Translation rows */}
                <View className="w-full px-6">
                  <TranslationRow
                    language={t("staff.localization.detail.english")}
                    value={data?.translations.en ?? ""}
                    onEdit={() => handleEditLocale("en")}
                    isLast={false}
                    editLabel={"staff.localization.detail.editTranslation"}
                  />
                  <TranslationRow
                    language={t("staff.localization.detail.arabic")}
                    value={data?.translations.ar ?? ""}
                    onEdit={() => handleEditLocale("ar")}
                    isLast={false}
                    editLabel={"staff.localization.detail.editTranslation"}
                  />
                  <TranslationRow
                    language={t("staff.localization.detail.chinese")}
                    value={data?.translations["zh-CN"] ?? ""}
                    onEdit={() => handleEditLocale("zh-CN")}
                    isLast
                    editLabel={"staff.localization.detail.editTranslation"}
                  />
                </View>
              </View>
            </ScrollView>
          </View>

          {/* Activity Log card — hidden until backend exposes audit trail (issue #434) */}
          {activityLog.length > 0 && (
            <View className="border border-stroke-50 rounded-xl overflow-hidden bg-white">
              <View className="px-6 py-4 border-b border-stroke-50">
                <AppText weight="semibold" className="text-lg text-dark-text-50">
                  {t("staff.localization.detail.activityLog")}
                </AppText>
              </View>
              <View className="px-6 py-6">
                {activityLog.map((entry, idx) => (
                  <ActivityEntry
                    key={entry.id}
                    entry={entry}
                    isLast={idx === activityLog.length - 1}
                    locale={locale}
                    t={t}
                  />
                ))}
              </View>
            </View>
          )}
        </View>

        <PageFooter />
      </ScrollView>

      {/* ── Edit Translation Modal ────────────────────────────── */}
      <EditTranslationModal
        visible={editAllVisible}
        onDismiss={handleDismissEdit}
        translationKey={decodedKey}
        locale={editLocale ?? undefined}
        onSave={handleSave}
        {...editInitialValues}
      />
    </View>
  );
}
